import { FormSchemaUtil } from "@/lib/FormSchemaUtil";
import { z } from "zod";
import { AuctionState, AuctionType } from "@prisma/client";

export const auctionCreationSchema = z.object({
  title: z
    .string()
    .min(1, {
      message: "Title is required",
    })
    .max(255),
  startDate: z.date().refine(FormSchemaUtil.checkIfDayInPast, {
    message: "Start date must not be in the past.",
  }),
  startTime: z.string().refine(FormSchemaUtil.isTime, {
    message: "Enter valid 12-hour time (hh:mm)",
  }),
  startMeridian: z.enum(["AM", "PM"]),
  endDate: z.date().refine(FormSchemaUtil.checkIfDayInPast, {
    message: "End date must not be in the past.",
  }),
  endTime: z.string().refine(FormSchemaUtil.isTime, {
    message: "Enter valid 12-hour time (hh:mm)",
  }),
  endMeridian: z.enum(["AM", "PM"]),
  auctionType: z.nativeEnum(AuctionType).default(AuctionType.ONLINE),
});

export const auctionPersistSchema = z.object({
  title: z
    .string()
    .min(1, {
      message: "Title is required",
    })
    .max(255),
  startDate: z.string(),
  endDate: z.string(),
  auctionType: z.nativeEnum(AuctionType),
  auctionState: z.nativeEnum(AuctionState),
});

export const lotSchema = z.object({
  title: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  startBid: z.coerce.number().positive(),
  appraisalMin: z.coerce.number().positive().nullable().optional(),
  appraisalMax: z.coerce.number().positive().nullable().optional(),
  cocode: z.string().max(255).nullable().optional(),
  additionalInfo: z.string().max(2000).nullable().optional(),
  category: z.string().max(255).nullable().optional(),
  inventoryNumber: z.coerce.number().positive().nullable().optional(),
  winnerInstructions: z.string().max(65536).nullable().optional(),
  isDisabled: z.boolean().optional(),
});

export const registerUserSchema = z
  .object({
    firstName: z.string().trim().min(1, "First name is required.").max(255),
    lastName: z.string().trim().min(1, "Last name is required.").max(255),
    email: z.string().email("Please enter a valid email."),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters.")
      .regex(/[0-9]/, "Password must contain at least one number.")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter."),
    confirmPassword: z
      .string()
  })
  .superRefine(({ confirmPassword, password }, ctx) => {
    if (confirmPassword !== password) {
      ctx.addIssue({
        code: "custom",
        message: "Passwords do not match.",
        path: ["confirmPassword"],
      });
    }
  });

export const emailAddressSchema = z.object({ email: z.string().email() });

export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters.")
      .regex(/[0-9]/, "Password must contain at least one number."),
    confirmPassword: z
      .string()
      .min(8, "Password must be at least 8 characters."),
  })
  .superRefine(({ confirmPassword, password }, ctx) => {
    if (confirmPassword !== password) {
      ctx.addIssue({
        code: "custom",
        message: "Passwords do not match.",
        path: ["confirmPassword"],
      });
    }
  });

export const addressSchema = z.object({
  country: z.string().min(3, {
    message: "Please select a country."
  }),
  region: z.string().min(1, {
    message: "Please select a region."
  }),
  address1: z.string().min(1, {
    message: "Please enter a street address."
  }),
  address2: z.string().nullish(),
  city: z.string().min(1, {
    message: "Please enter a city."
  }),
  postalCode: z.string().min(1, {
    message: "Please enter your postal code."
  })
})

export const bidderGroupSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  buyerPremium: z.number()
    .min(0, 'Buyer premium must be non-negative.')
    .max(100, 'Buyer premium cannot exceed 100%.')
    .transform(val => Number(val.toFixed(1))) // Ensure 1 decimal point precision
});

export const consignorTierSchema = z.object({
  id: z.number(),
  tierRate: z.number({
    invalid_type_error: "Please check your commission rates.",
    required_error: "Commission rate is required."
  })
    .min(0, 'Commission rate must be non-negative.')
    .max(100, 'Commission rate cannot exceed 100%.')
    .transform(val => Number(val.toFixed(1))),
  tierRangeEnd: z.number(),
  consignorGroupId: z.number()
});

export const consignorGroupSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  minimumCommission: z.number({
    invalid_type_error: "Minimum commission must be a number.",
  })
    .min(0, 'Minimum commission must be non-negative.')
    .max(100, 'Minimum commission cannot exceed 100%.')
    .transform(val => Number(val.toFixed(1)))
});